import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DoctorLayout from "@/pages/doctor/DoctorLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { useIsGracePeriod } from "@/components/GracePeriodBanner";

const INVESTIGATION_OPTIONS = ["HbA1c", "Fasting Sugar", "PPBS", "Lipid Profile", "Creatinine", "eGFR", "Urine Albumin", "CBC", "LFT", "Thyroid"];
const EMPTY_MEDICINE = { name: "", strength: "", dosage: "", frequency: "", food_timing: "", duration: "", instructions: "" };

export default function PrescriptionEditPage() {
  const { prescriptionId } = useParams();
  const navigate = useNavigate();
  const isReadOnly = useIsGracePeriod();
  const [patient, setPatient] = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [medicines, setMedicines] = useState([{ ...EMPTY_MEDICINE }]);
  const [dietAdvice, setDietAdvice] = useState("");
  const [exerciseAdvice, setExerciseAdvice] = useState("");
  const [lifestyleAdvice, setLifestyleAdvice] = useState("");
  const [investigations, setInvestigations] = useState([]);
  const [customInvestigation, setCustomInvestigation] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupNotes, setFollowupNotes] = useState("");
  const [weight, setWeight] = useState("");
  const [bp, setBp] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/prescriptions/${prescriptionId}`).then(async ({ data }) => {
      setPrescription(data);
      setMedicines(
        data.medicines?.length
          ? data.medicines.map((m) => ({ ...m, _key: crypto.randomUUID() }))
          : [{ ...EMPTY_MEDICINE, _key: crypto.randomUUID() }]
      );
      setDietAdvice(data.diet_advice || "");
      setExerciseAdvice(data.exercise_advice || "");
      setLifestyleAdvice(data.lifestyle_advice || "");
      setInvestigations(data.investigations || []);
      setCustomInvestigation((data.investigations || []).filter((i) => !INVESTIGATION_OPTIONS.includes(i)).join(", "));
      setFollowupDate(data.followup_date || "");
      setFollowupNotes(data.followup_notes || "");
      setWeight(data.clinical_weight || "");
      setBp(data.clinical_bp || "");
      setNotes(data.clinical_notes || "");
      const { data: p } = await api.get(`/patients/${data.patient_id}`);
      setPatient(p);
    });
  }, [prescriptionId]);

  const updateMedicine = (i, field, value) => {
    setMedicines((meds) => meds.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)));
  };

  const toggleInvestigation = (name) => {
    setInvestigations((inv) => (inv.includes(name) ? inv.filter((i) => i !== name) : [...inv, name]));
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const checklistSelected = investigations.filter((i) => INVESTIGATION_OPTIONS.includes(i));
      const customInvestigations = customInvestigation.split(",").map((s) => s.trim()).filter(Boolean);
      await api.put(`/prescriptions/${prescriptionId}`, {
        medicines: medicines.filter((m) => m.name),
        diet_advice: dietAdvice,
        exercise_advice: exerciseAdvice,
        lifestyle_advice: lifestyleAdvice,
        investigations: [...checklistSelected, ...customInvestigations],
        followup_date: followupDate || null,
        followup_notes: followupNotes,
        clinical_weight: weight,
        clinical_bp: bp,
        clinical_notes: notes,
      });
      toast.success("Prescription updated");
      navigate(`/doctor/patients/${prescription.patient_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (!patient || !prescription) return <DoctorLayout><p className="text-slate-500">Loading...</p></DoctorLayout>;

  return (
    <DoctorLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="edit-prescription-heading">
            Edit Prescription — {patient.full_name}
          </h1>
          <p className="text-sm text-slate-500 font-mono">{patient.patient_code} · Originally saved {new Date(prescription.created_at).toLocaleDateString()}</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/doctor/patients/${prescription.patient_id}`)}>Back to Profile</Button>
      </div>

      <div className="mt-6 card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
        <h2 className="font-semibold text-slate-900">Clinical Examination</h2>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div><Label>Weight (kg)</Label><Input data-testid="edit-weight" value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
          <div><Label>Blood Pressure (mm Hg)</Label><Input data-testid="edit-bp" value={bp} onChange={(e) => setBp(e.target.value)} /></div>
          <div className="sm:col-span-1"><Label>General Notes</Label><Input data-testid="edit-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
      </div>

      <div className="mt-6 card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Medicines</h2>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setMedicines((m) => [...m, { ...EMPTY_MEDICINE, _key: crypto.randomUUID() }])} data-testid="edit-add-medicine-btn">
            <Plus className="h-4 w-4" /> Add Medicine
          </Button>
        </div>
        <div className="mt-4 space-y-3">
          {medicines.map((m, i) => (
            <div key={m._key} className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-7" data-testid={`edit-medicine-row-${i}`}>
              <Input placeholder="Name" data-testid={`edit-medicine-name-${i}`} value={m.name} onChange={(e) => updateMedicine(i, "name", e.target.value)} />
              <Input placeholder="Strength" value={m.strength} onChange={(e) => updateMedicine(i, "strength", e.target.value)} />
              <Input placeholder="Dosage" value={m.dosage} onChange={(e) => updateMedicine(i, "dosage", e.target.value)} />
              <Input placeholder="Frequency" value={m.frequency} onChange={(e) => updateMedicine(i, "frequency", e.target.value)} />
              <Input placeholder="Before/After Food" value={m.food_timing} onChange={(e) => updateMedicine(i, "food_timing", e.target.value)} />
              <Input placeholder="Duration" value={m.duration} onChange={(e) => updateMedicine(i, "duration", e.target.value)} />
              <div className="flex items-center gap-1">
                <Input placeholder="Instructions" value={m.instructions} onChange={(e) => updateMedicine(i, "instructions", e.target.value)} />
                <button data-testid={`edit-remove-medicine-${i}`} onClick={() => setMedicines((meds) => meds.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <h2 className="font-semibold text-slate-900">Lifestyle Advice</h2>
          <div className="mt-3 space-y-3">
            <div><Label>Diet</Label><Textarea data-testid="edit-diet" value={dietAdvice} onChange={(e) => setDietAdvice(e.target.value)} /></div>
            <div><Label>Exercise</Label><Textarea data-testid="edit-exercise" value={exerciseAdvice} onChange={(e) => setExerciseAdvice(e.target.value)} /></div>
            <div><Label>Lifestyle Instructions</Label><Textarea data-testid="edit-lifestyle" value={lifestyleAdvice} onChange={(e) => setLifestyleAdvice(e.target.value)} /></div>
          </div>
        </div>

        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <h2 className="font-semibold text-slate-900">Recommended Investigations</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {INVESTIGATION_OPTIONS.map((opt) => (
              <button
                key={opt}
                data-testid={`edit-investigation-${opt.replace(/\s/g, "-")}`}
                onClick={() => toggleInvestigation(opt)}
                className={`rounded-full px-3 py-1 text-xs font-medium border ${investigations.includes(opt) ? "bg-teal-700 text-white border-teal-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                {opt}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <Label>Other Investigation (custom, comma-separated)</Label>
            <Input
              data-testid="edit-custom-investigation-input"
              placeholder="e.g. Vitamin D, Chest X-Ray"
              value={customInvestigation}
              onChange={(e) => setCustomInvestigation(e.target.value)}
            />
          </div>
          <h2 className="mt-6 font-semibold text-slate-900">Follow-up</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Next Visit Date</Label><DatePicker testId="edit-followup-date" value={followupDate} onChange={setFollowupDate} fromYear={new Date().getFullYear()} /></div>
            <div><Label>Additional Notes</Label><Input data-testid="edit-followup-notes" value={followupNotes} onChange={(e) => setFollowupNotes(e.target.value)} /></div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button data-testid="save-edit-prescription-btn" className="gap-2 bg-teal-700 hover:bg-teal-800" disabled={saving || isReadOnly} title={isReadOnly ? "Disabled during subscription grace period" : undefined} onClick={saveChanges}>
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Changes"}
        </Button>
        <Button variant="outline" onClick={() => navigate(`/doctor/patients/${prescription.patient_id}`)}>Cancel</Button>
      </div>
    </DoctorLayout>
  );
}
